import { useState } from "react";
import { PlayCircle } from "lucide-react";

/**
 * FounderVideo — YouTube/Vimeo lazy-embed with elegant "video coming soon"
 * placeholder. Set `videoId` to a YouTube video ID (e.g. "dQw4w9WgXcQ")
 * and this becomes a full lazy-loaded embed. Leave blank and it shows
 * a professional placeholder card that says a video is coming soon —
 * so the section stays polished without a broken embed.
 *
 * Uses "youtube-nocookie.com" domain for GDPR / cookie compliance and
 * click-to-load pattern for Core Web Vitals (no third-party JS on
 * initial paint).
 */
export const FounderVideo = ({
  videoId,
  posterSrc,
  posterAlt = "Dr. Sumit Chhabra, Founder & CEO of Asia Sourcing",
  title = "The founder's story · 23 years on the factory floor",
  duration = null,
}) => {
  const [playing, setPlaying] = useState(false);

  return (
    <div
      data-testid="founder-video"
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        background: "var(--ink)",
        overflow: "hidden",
        borderRadius: 0,
      }}
    >
      {playing && videoId ? (
        <iframe
          data-testid="founder-video-iframe"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      ) : (
        <button
          type="button"
          data-testid="founder-video-play"
          onClick={() => videoId && setPlaying(true)}
          disabled={!videoId}
          aria-label={videoId ? `Play: ${title}` : "Founder video coming soon"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            padding: 0,
            border: 0,
            cursor: videoId ? "pointer" : "default",
            background: "transparent",
            display: "block",
          }}
        >
          {posterSrc && (
            <img
              src={posterSrc}
              alt={posterAlt}
              loading="lazy"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 8%",
              }}
            />
          )}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at center, rgba(15,17,21,.55) 0%, rgba(15,17,21,.75) 60%, rgba(15,17,21,.92) 100%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--paper)",
              textAlign: "center",
              padding: "2rem 1.4rem",
            }}
          >
            <PlayCircle
              size={72}
              strokeWidth={1}
              color={videoId ? "var(--gold-muted, #C5A880)" : "rgba(197,168,128,.55)"}
              aria-hidden="true"
              style={{ marginBottom: "1rem" }}
            />
            <p
              className="mono"
              style={{
                color: "var(--gold-muted, #C5A880)",
                fontSize: ".7rem",
                letterSpacing: ".16em",
                textTransform: "uppercase",
                margin: "0 0 .6rem",
              }}
            >
              {videoId ? "Watch · Founder story" : "Founder story · coming soon"}
            </p>
            <p
              style={{
                fontFamily: "var(--display, Fraunces)",
                fontSize: "clamp(1.1rem, 2.2vw, 1.4rem)",
                letterSpacing: "-.01em",
                lineHeight: 1.25,
                margin: 0,
                maxWidth: "36ch",
              }}
            >
              {title}
            </p>
            {duration && (
              <p
                className="mono"
                style={{
                  color: "rgba(248,250,252,.7)",
                  fontSize: ".72rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: "1rem 0 0",
                }}
              >
                {duration}
              </p>
            )}
            {!videoId && (
              <p
                style={{
                  color: "rgba(248,250,252,.72)",
                  fontSize: ".85rem",
                  margin: "1.2rem 0 0",
                  maxWidth: "44ch",
                  lineHeight: 1.55,
                }}
              >
                A short film on twenty-three years of India sourcing — filming in progress.
              </p>
            )}
          </div>
        </button>
      )}
    </div>
  );
};

export default FounderVideo;
